/*
 * Copyright (C) 2019 Frontier Developments PLC.
 */

import {Component} from 'vue-property-decorator';
import {Action} from 'vuex-class';
import WebFontLoader from 'webfontloader';
import pkce from '@/services/pkce';
import BuildVersion from '@/components/BuildVersion/index.vue';
import {eventBus} from '@/services/eventbus';
import {mixins} from 'vue-class-component';
import Scroll from '@/mixins/Scroll';

/**
 * Application component.
 *
 * This is the main entry point component for the vue application.  The component will load global styles and router-led
 * layout (e.g. header, content, footer) that will wrap views.
 *
 * @author Jonny Cook <jcook@frontier.co.uk>
 */
@Component({
    components: {
        'c-build-version': BuildVersion,
    },
})
export default class App extends mixins(Scroll) {
    @Action('updateMedia', {namespace: 'settingsModule'}) public updateMedia: any;
    @Action('setCash', {namespace: 'gameModule'}) public setCash!: (cash: number) => void;

    /**
     * Before created life-cycle event.
     */
    public beforeCreate(): void {
        pkce.initialise();
        WebFontLoader.load({
            google: {
                families: ['Noto Sans:400,700'],
            },
            active: (): void => {
                sessionStorage.setItem('fonts', 'true');
            },
        });

        document.body.classList.add(process.env.NODE_ENV || '');
    }

    /**
     * Destroy life-cycle event removes the refresh listener to prevent multiple listeners being generate during the
     * life of the application.
     */
    public beforeDestroy(): void {
        eventBus.$off('cmd-balance');
    }

    /**
     * Created life-cycle event.
     *
     * Runs media/screen identification script.
     */
    public async created(): Promise<void> {
        // configure listener
        eventBus.$on('cmd-balance', async (cash: number = 0): Promise<void> => {
            this.setCash(cash);
        });

        // Screen size identification
        const wnd: any = window;
        const documentElement: any = document.documentElement;

        ((m: ((mediaQuery: string) => MediaQueryList)|null|undefined, d: DOMTokenList, a: string, b: string): void => {
            if (m && d) {
                d.add('c1', 'c2');
                if (d.contains('c2')) {
                    const q: MediaQueryList[] = [m(a + 0 + b), m(a + 42.5 + b), m(a + 71.25 + b), m(a + 90 + b)];

                    /**
                     * Add media to DOM.
                     *
                     * @param {string} media
                     */
                    const add: (...media: string[]) => void = (...media: string[]): void => {
                        d.add(...media);
                        this.updateMedia(media);
                    };

                    /**
                     * Remove media from DOM.
                     *
                     * @param {string} media
                     */
                    const remove: (...media: string[]) => void = (...media: string[]): void => {
                        d.remove(...media);
                        this.updateMedia([]);
                    };

                    /**
                     * Listener event action.
                     *
                     * @param {MediaQueryList} r
                     */
                    const f: (r: any) => void = (r: any): void => {
                        let p: string = 'desktop';
                        remove('c1', 'c2', 'phone', 'tablet', p, 'widescreen', 'lte-tablet', 'gte-tablet', 'lte-' + p,
                            'gte-' + p);
                        if (q[3].matches) {
                            p = 'widescreen';
                            add(p, 'gte-tablet', 'gte-desktop');
                        } else if (q[2].matches) {
                            add(p, 'gte-tablet', 'gte-' + p, 'lte-' + p);
                        } else if (q[1].matches) {
                            p = 'tablet';
                            add(p, 'lte-' + p, 'gte-' + p, 'lte-desktop');
                        } else if (q[0].matches) {
                            p = 'phone';
                            add(p, 'lte-tablet', 'lte-desktop');
                        } else {
                            add(p, 'gte-tablet', 'gte-' + p, 'lte-' + p);
                        }
                    };

                    for (let j: number = 0; j < 4; j++) {
                        f(q[j]);
                        q[j].addListener(f);
                    }
                }
            }
        })(wnd.matchMedia, documentElement.classList, '(min-width:', 'em)');
    }
}
